import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { isNil } from 'lodash-es';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUI } from 'src/ui/contexts/useUIState';
import { type Page } from 'src/ui/pages';

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  height: theme.spacing(4),
}));

export function PageComponent({
  page,
  dense,
  ml = 0,
}: {
  page: Page;
  dense: boolean;
  ml?: number;
}) {
  const { pathname } = useLocation();

  const [isExpanded, setIsExpanded] = React.useState(true);
  const isExpandable = page.component;
  const isSelected = !isNil(page.path) && pathname.includes(page.path ?? '');
  const { handleCloseDrawer } = useUI();
  const handleClick = (_event: React.SyntheticEvent) => {
    setIsExpanded((prevState) => !prevState);
  };

  const handleLinkButtonClick = () => {
    handleCloseDrawer();
  };

  const ListComponent = (
    <ListItem
      disablePadding
      {...(isExpandable &&
        !dense && {
          secondaryAction: (
            <IconButton
              size={'small'}
              edge="end"
              disableRipple
              aria-label="comments"
              onClick={handleClick}
              sx={{ mt: -0.5 }}
            >
              {isExpandable && (
                <>{isExpanded ? <ExpandLess /> : <ExpandMore />}</>
              )}
            </IconButton>
          ),
        })}
      key={page.title}
    >
      <StyledListItemButton
        selected={isSelected}
        {...(page.path && { component: Link, to: page.path })}
        sx={{ borderRadius: 1, mb: 0.5, pl: 0.5 }}
        onClick={handleLinkButtonClick}
      >
        <ListItemIcon sx={{ minWidth: 56 }}>{page.icon}</ListItemIcon>
        {!dense && (
          <>
            <ListItemText
              sx={(theme) => ({
                '& .MuiListItemText-primary': {
                  fontWeight: isSelected
                    ? theme.typography.fontWeightBold
                    : theme.typography.fontWeightRegular,
                },
              })}
              primary={page.title}
            />
          </>
        )}
      </StyledListItemButton>
    </ListItem>
  );

  return (
    <Box
      sx={{
        ml,
      }}
    >
      {page.addDividers && (
        <Divider
          sx={(theme) => ({
            mt: 0.5,
            mb: 1,
            mx: 1,
            borderColor: theme.palette.divider,
          })}
        />
      )}
      {dense ? (
        <Tooltip title={page.title} arrow placement={'right'}>
          {ListComponent}
        </Tooltip>
      ) : (
        ListComponent
      )}
      {(page.component || page.subPaths) && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {page.component}
          {page.subPaths?.map((subPath) => (
            <PageComponent
              key={subPath.title}
              page={subPath}
              dense={dense}
              ml={dense ? 0 : 3}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}
